import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Book, Cloud } from 'lucide-react';

export default function Documentation() {
  const [activeTab, setActiveTab] = useState('admin-panel');

  return (
    <div data-testid="documentation-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Documentation</h1>
        <p className="text-slate-600">Complete guides and references for the admin panel</p>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-slate-200 bg-slate-50 px-6">
            <TabsList className="bg-transparent">
              <TabsTrigger 
                value="admin-panel" 
                data-testid="admin-panel-tab"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Book className="w-4 h-4 mr-2" />
                Admin Panel Guide
              </TabsTrigger>
              <TabsTrigger 
                value="gcs-setup" 
                data-testid="gcs-setup-tab"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Cloud className="w-4 h-4 mr-2" />
                GCS Setup Guide
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="admin-panel" className="p-6 md:p-8">
            <div className="prose prose-slate max-w-none">
              <div className="documentation-content">
                <h1 className="text-3xl font-bold text-slate-800 mb-6">Loopai Admin Control Panel</h1>
                
                <p className="text-lg text-slate-600 mb-6">
                  A comprehensive full-stack admin dashboard for managing Loopai platform users, transactions, credits, projects, and assets.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">🌐 Live URL</h3>
                  <p className="text-blue-800">
                    <strong>Admin Panel:</strong> <a href="https://ai-model-hub-47.preview.emergentagent.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://ai-model-hub-47.preview.emergentagent.com</a>
                  </p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">🔐 Admin Credentials</h3>
                  <ul className="text-purple-800 list-none">
                    <li><strong>Username:</strong> <code className="bg-purple-200 px-2 py-1 rounded">admin</code></li>
                    <li><strong>Password:</strong> <code className="bg-purple-200 px-2 py-1 rounded">admin123</code></li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🎯 Features</h2>

                <div className="space-y-6">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">1. Dashboard Overview</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• Real-time statistics display</li>
                      <li>• Total users count</li>
                      <li>• Total transactions count</li>
                      <li>• Total projects count</li>
                      <li>• Total assets count</li>
                      <li>• Total revenue calculation</li>
                      <li>• Total credits used tracking</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">2. Users Management</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• View all registered users</li>
                      <li>• Search by name or email</li>
                      <li>• Display user details: Name, Email, Phone</li>
                      <li>• Current credits balance</li>
                      <li>• Account status (active/inactive)</li>
                      <li>• Number of projects</li>
                      <li>• Joined date</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">3. Transactions Management</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• View all platform transactions</li>
                      <li>• Search by user email</li>
                      <li>• Transaction amount and credits</li>
                      <li>• Transaction type (purchase/refund/bonus)</li>
                      <li>• Transaction status (completed/pending/failed)</li>
                      <li>• Transaction timestamp</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">4. Credits Management</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• View user credit balances</li>
                      <li>• <strong>Edit credits manually</strong> for any user</li>
                      <li>• Real-time credit updates</li>
                      <li>• Transaction logging for credit modifications</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">5. Projects Management</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• Complete project overview</li>
                      <li>• Filter by status (completed/in-progress/failed)</li>
                      <li>• Filter by type (video/image/audio)</li>
                      <li>• Search by project name or user email</li>
                      <li>• View credits used per project</li>
                      <li>• Created date tracking</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-pink-500 pl-4">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">6. Assets Management</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• Manage platform assets (images, voices, elements)</li>
                      <li>• Filter assets by type</li>
                      <li>• Search by asset name</li>
                      <li>• View file size and uploader</li>
                      <li>• Delete assets functionality</li>
                      <li>• <strong>Google Cloud Storage integration ready</strong></li>
                    </ul>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🛠 Tech Stack</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-slate-800 mb-3">Backend</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• <strong>Framework:</strong> FastAPI</li>
                      <li>• <strong>Database:</strong> MongoDB (Motor async driver)</li>
                      <li>• <strong>Authentication:</strong> JWT with Bearer tokens</li>
                      <li>• <strong>Password Hashing:</strong> bcrypt</li>
                      <li>• <strong>Storage:</strong> Google Cloud Storage</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-slate-800 mb-3">Frontend</h3>
                    <ul className="text-slate-600 space-y-1">
                      <li>• <strong>Framework:</strong> React 19</li>
                      <li>• <strong>Routing:</strong> React Router v7</li>
                      <li>• <strong>UI Components:</strong> Shadcn/UI</li>
                      <li>• <strong>Styling:</strong> Tailwind CSS</li>
                      <li>• <strong>HTTP Client:</strong> Axios</li>
                      <li>• <strong>Notifications:</strong> Sonner</li>
                    </ul>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🔐 Security Features</h2>
                
                <div className="bg-green-50 p-6 rounded-lg">
                  <ol className="text-slate-700 space-y-2 list-decimal list-inside">
                    <li><strong>JWT Authentication:</strong> All API endpoints (except login) require Bearer token authentication</li>
                    <li><strong>Password Hashing:</strong> Admin passwords stored using bcrypt</li>
                    <li><strong>Protected Routes:</strong> Frontend routes protected with authentication checks</li>
                    <li><strong>CORS Configuration:</strong> Configurable CORS origins</li>
                    <li><strong>Token Expiration:</strong> JWT tokens expire after 8 hours</li>
                  </ol>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🚀 API Endpoints</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Authentication</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• POST /api/admin/login - Admin login</li>
                      <li>• GET /api/admin/verify - Verify JWT token</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Dashboard</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• GET /api/dashboard/stats - Get dashboard statistics</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Users</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• GET /api/users - Get all users</li>
                      <li>• POST /api/users - Create user</li>
                      <li>• PUT /api/users/&#123;user_id&#125; - Update user</li>
                      <li>• DELETE /api/users/&#123;user_id&#125; - Delete user</li>
                      <li>• PUT /api/users/&#123;user_id&#125;/credits - Update user credits</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Transactions</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• GET /api/transactions - Get all transactions</li>
                      <li>• POST /api/transactions - Create transaction</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Projects</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• GET /api/projects - Get all projects</li>
                      <li>• POST /api/projects - Create project</li>
                      <li>• DELETE /api/projects/&#123;project_id&#125; - Delete project</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Assets</h3>
                    <ul className="text-slate-600 font-mono text-sm space-y-1 bg-slate-50 p-4 rounded">
                      <li>• GET /api/assets - Get all assets</li>
                      <li>• POST /api/assets - Create asset</li>
                      <li>• DELETE /api/assets/&#123;asset_id&#125; - Delete asset</li>
                    </ul>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🧪 Testing Results</h2>
                
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-slate-700 mb-4">The application has been thoroughly tested:</p>
                  <ul className="text-slate-700 space-y-2">
                    <li>• <strong>Backend API:</strong> 90.9% success rate</li>
                    <li>• <strong>Frontend UI:</strong> 95% success rate</li>
                    <li>• <strong>Overall:</strong> 93% success rate</li>
                  </ul>
                  <p className="text-slate-700 mt-4">All major features tested and working:</p>
                  <div className="grid md:grid-cols-2 gap-2 mt-2">
                    <div>✅ Authentication flow</div>
                    <div>✅ Dashboard statistics</div>
                    <div>✅ User management</div>
                    <div>✅ Transaction tracking</div>
                    <div>✅ Credit editing</div>
                    <div>✅ Project filtering</div>
                    <div>✅ Asset management</div>
                    <div>✅ Search functionality</div>
                    <div>✅ Navigation</div>
                    <div>✅ Protected routes</div>
                  </div>
                  <p className="text-slate-700 mt-4 font-semibold">💵 Currency: Indian Rupees (INR ₹)</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gcs-setup" className="p-6 md:p-8">
            <div className="prose prose-slate max-w-none">
              <div className="documentation-content">
                <h1 className="text-3xl font-bold text-slate-800 mb-6">Google Cloud Storage Setup Guide</h1>
                
                <p className="text-lg text-slate-600 mb-6">
                  This guide will help you configure Google Cloud Storage for the Loopai Admin Panel asset management.
                </p>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <h3 className="text-lg font-semibold text-orange-900 mb-2">📋 Prerequisites</h3>
                  <ul className="text-orange-800">
                    <li>• Google Cloud Platform account</li>
                    <li>• A GCP project created</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Step-by-Step Setup</h2>

                <div className="space-y-6">
                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">1. Create a GCS Bucket</h3>
                    <ol className="text-slate-600 space-y-2 list-decimal list-inside">
                      <li>Go to <a href="https://console.cloud.google.com/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                      <li>Navigate to <strong>Cloud Storage</strong> &gt; <strong>Buckets</strong></li>
                      <li>Click <strong>Create Bucket</strong></li>
                      <li>Configure your bucket:
                        <ul className="ml-6 mt-2 space-y-1">
                          <li>• <strong>Name:</strong> Choose a unique name (e.g., <code className="bg-slate-200 px-2 py-1 rounded">loopai-assets-prod</code>)</li>
                          <li>• <strong>Location:</strong> Choose closest to your users</li>
                          <li>• <strong>Storage class:</strong> Standard (for frequently accessed data)</li>
                          <li>• <strong>Access control:</strong> Fine-grained</li>
                        </ul>
                      </li>
                      <li>Click <strong>Create</strong></li>
                    </ol>
                  </div>

                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">2. Create a Service Account</h3>
                    <ol className="text-slate-600 space-y-2 list-decimal list-inside">
                      <li>Navigate to <strong>IAM & Admin</strong> &gt; <strong>Service Accounts</strong></li>
                      <li>Click <strong>Create Service Account</strong></li>
                      <li>Configure:
                        <ul className="ml-6 mt-2 space-y-1">
                          <li>• <strong>Name:</strong> <code className="bg-slate-200 px-2 py-1 rounded">loopai-admin-storage</code></li>
                          <li>• <strong>Description:</strong> Service account for Loopai admin panel asset management</li>
                        </ul>
                      </li>
                      <li>Click <strong>Create and Continue</strong></li>
                    </ol>
                  </div>

                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">3. Grant Permissions</h3>
                    <ol className="text-slate-600 space-y-2 list-decimal list-inside">
                      <li>Add the following role: <strong>Storage Admin</strong> (roles/storage.admin)</li>
                      <li>Click <strong>Continue</strong> and then <strong>Done</strong></li>
                    </ol>
                  </div>

                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">4. Create and Download JSON Key</h3>
                    <ol className="text-slate-600 space-y-2 list-decimal list-inside">
                      <li>Find your newly created service account in the list</li>
                      <li>Click on it to open details</li>
                      <li>Go to the <strong>Keys</strong> tab</li>
                      <li>Click <strong>Add Key</strong> &gt; <strong>Create new key</strong></li>
                      <li>Select <strong>JSON</strong> format</li>
                      <li>Click <strong>Create</strong></li>
                      <li>The JSON key file will be downloaded automatically</li>
                      <li className="text-red-600 font-semibold">⚠️ Keep this file secure! It contains credentials to access your GCS bucket</li>
                    </ol>
                  </div>

                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">5. Configure the Admin Panel</h3>
                    
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-slate-700 mb-2">Option A: Using Base64 Encoding</h4>
                      <ol className="text-slate-600 space-y-2 list-decimal list-inside">
                        <li>Open terminal and navigate to where you saved the JSON key file</li>
                        <li>Encode the JSON file to base64:
                          <pre className="bg-slate-800 text-white p-3 rounded mt-2 overflow-x-auto">
                            <code>base64 -w 0 service-account-key.json &gt; encoded-key.txt</code>
                          </pre>
                        </li>
                        <li>Copy the contents of <code className="bg-slate-200 px-2 py-1 rounded">encoded-key.txt</code></li>
                        <li>Update <code className="bg-slate-200 px-2 py-1 rounded">/app/backend/.env</code>:
                          <pre className="bg-slate-800 text-white p-3 rounded mt-2 overflow-x-auto">
                            <code>GCS_CREDENTIALS="&lt;paste-base64-encoded-string-here&gt;"{`
`}GCS_BUCKET_NAME="your-bucket-name"</code>
                          </pre>
                        </li>
                      </ol>
                    </div>
                  </div>

                  <div className="border rounded-lg p-6 bg-slate-50">
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">6. Restart the Backend</h3>
                    <p className="text-slate-600 mb-2">After configuring the credentials:</p>
                    <pre className="bg-slate-800 text-white p-3 rounded overflow-x-auto">
                      <code>sudo supervisorctl restart backend</code>
                    </pre>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🔒 Security Best Practices</h2>
                
                <div className="bg-red-50 p-6 rounded-lg">
                  <ol className="text-slate-700 space-y-3 list-decimal list-inside">
                    <li><strong>Never commit credentials to version control</strong>
                      <ul className="ml-6 mt-1 space-y-1 list-disc">
                        <li>Add <code className="bg-red-200 px-2 py-1 rounded">gcs-credentials.json</code> to <code className="bg-red-200 px-2 py-1 rounded">.gitignore</code></li>
                        <li>Never expose base64 encoded credentials in public repos</li>
                      </ul>
                    </li>
                    <li><strong>Use least privilege principle</strong>
                      <ul className="ml-6 mt-1 space-y-1 list-disc">
                        <li>Only grant Storage Admin if upload/delete is needed</li>
                        <li>Use Storage Object Viewer for read-only access</li>
                      </ul>
                    </li>
                    <li><strong>Enable audit logging</strong>
                      <ul className="ml-6 mt-1 space-y-1 list-disc">
                        <li>Enable Cloud Audit Logs for your bucket</li>
                        <li>Monitor for unauthorized access</li>
                      </ul>
                    </li>
                    <li><strong>Set bucket lifecycle policies</strong>
                      <ul className="ml-6 mt-1 space-y-1 list-disc">
                        <li>Auto-delete temporary files after X days</li>
                        <li>Move old assets to cheaper storage classes</li>
                      </ul>
                    </li>
                  </ol>
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">🔧 Troubleshooting</h2>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-red-500 pl-4 py-2">
                    <h4 className="font-semibold text-slate-800">Error: "GCS not configured"</h4>
                    <ul className="text-slate-600 text-sm mt-1 space-y-1">
                      <li>• Check if <code className="bg-slate-200 px-2 py-1 rounded">GCS_CREDENTIALS</code> is properly set in <code className="bg-slate-200 px-2 py-1 rounded">.env</code></li>
                      <li>• Verify base64 encoding is correct</li>
                      <li>• Ensure the JSON key file is valid</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4 py-2">
                    <h4 className="font-semibold text-slate-800">Error: "Permission denied"</h4>
                    <ul className="text-slate-600 text-sm mt-1 space-y-1">
                      <li>• Verify service account has Storage Admin role</li>
                      <li>• Check if bucket name is correct</li>
                      <li>• Ensure service account email has access to the bucket</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-yellow-500 pl-4 py-2">
                    <h4 className="font-semibold text-slate-800">Error: "Bucket not found"</h4>
                    <ul className="text-slate-600 text-sm mt-1 space-y-1">
                      <li>• Verify <code className="bg-slate-200 px-2 py-1 rounded">GCS_BUCKET_NAME</code> matches your actual bucket name</li>
                      <li>• Check if bucket exists in GCP Console</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg mt-8">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">📚 Additional Resources</h3>
                  <ul className="text-blue-800 space-y-1">
                    <li>• <a href="https://cloud.google.com/storage/docs" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Cloud Storage Documentation</a></li>
                    <li>• <a href="https://googleapis.dev/python/storage/latest/index.html" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">GCS Python Client Library</a></li>
                    <li>• <a href="https://cloud.google.com/storage/docs/best-practices" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Best Practices for Cloud Storage</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
